import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslationService } from '@core/services/translation.service';
import { environment } from '../../../environments/environment';

/** Mirrors `LOCALE_HEADER` in `skiss_dev_tools.helpers.locale.context`. */
export const LOCALE_HEADER = 'X-Skiss-Locale';

/**
 * Tells the API which language the app is currently showing.
 *
 * The language picker is client-side: it swaps the UI strings and never writes
 * `user_settings.localization`. Everything the server renders — lesson titles
 * and prose, module and test names, course names, cart lines — was therefore
 * answered in whatever language the *account* was created with, so switching to
 * Ukrainian localised the chrome and left the content in English.
 *
 * An interceptor rather than a parameter on each read: the alternative is
 * threading a locale through every endpoint, every service method and every
 * call site, which is a great deal of signature to get right for one string.
 * The server treats the header as a request, not an authority — the stored
 * setting still wins where nothing asks, which is every path it initiates
 * itself.
 *
 * Scoped to our own API. A third party has no use for the header, and sending
 * it would leak a preference to hosts that never asked for one.
 */
export const localeInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) {
    return next(req);
  }

  const locale = inject(TranslationService).currentLocaleSignal();
  return next(locale ? req.clone({ setHeaders: { [LOCALE_HEADER]: locale } }) : req);
};
